import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { StepperModule } from 'primeng/stepper';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { FormFieldComponent } from '../../../shared/components/form-field/form-field.component';
import { I18nService } from '../../service/i18n.service';
import { NotificacaoService } from '../../service/notificacao.service';
import { ToolsService } from '../../service/tools.service';
import { RedefinirSenhaModel } from './models/redefinir-senha.model';
import { RecuperarSenhaService } from './service/recuperar-senha.service';

type TipoRecuperacao = 'telefone' | 'email';

@Component({
  selector: 'app-recuperar-senha',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    // PrimeNG
    StepperModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    ConfirmDialogModule,
    ToggleButtonModule,
    // Custom Components
    FormFieldComponent,
    InputMaskModule,
  ],
  templateUrl: './recuperar-senha.component.html',
  styleUrls: ['./recuperar-senha.component.scss'],
  providers: [ConfirmationService],
})
export class RecuperarSenhaComponent implements OnInit {
  // ========================================
  // PROPRIEDADES
  // ========================================
  active = 0;
  isLoading = signal<boolean>(false);
  tipoRecuperacao = signal<TipoRecuperacao>('telefone');

  // Formulários
  formContato: FormGroup = {} as FormGroup;
  formTokenSenhas: FormGroup = {} as FormGroup;

  // Validação de senha
  hasLowerCase = signal<boolean>(false);
  hasUpperCase = signal<boolean>(false);
  hasNumber = signal<boolean>(false);
  hasSpecialChar = signal<boolean>(false);
  hasMinLength = signal<boolean>(false);
  regexSenha = '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})';

  // ========================================
  // INJEÇÃO DE DEPENDÊNCIAS
  // ========================================
  private formBuilder = inject(FormBuilder);
  private notificacaoService = inject(NotificacaoService);
  private translate = inject(TranslateService);
  private i18nService = inject(I18nService);
  private recuperarSenhaService = inject(RecuperarSenhaService);
  private router = inject(Router);
  private confirmationService = inject(ConfirmationService);
  private primeNGConfig = inject(PrimeNGConfig);

  // ========================================
  // LIFECYCLE HOOKS
  // ========================================
  constructor() {
    this.translate.use(this.i18nService.idioma.value.code);
  }

  ngOnInit(): void {
    this.criarFormularios();
    this.configurarValidacaoSenha();
    this.configurarTraducaoPrimeNG();
  }

  private configurarTraducaoPrimeNG(): void {
    this.primeNGConfig.setTranslation({
      weak: 'Fraca',
      medium: 'Média',
      strong: 'Forte',
      passwordPrompt: 'Digite uma senha',
    });
  }

  // ========================================
  // MÉTODOS DE INICIALIZAÇÃO
  // ========================================
  private criarFormularios(): void {
    this.formContato = this.formBuilder.group({
      telefone: [
        '',
        [Validators.required, Validators.pattern(/^\(\d{2}\)\s\d{4,5}-\d{4}$/)],
      ],
      email: ['', [Validators.required, Validators.email]],
    });

    this.formTokenSenhas = this.formBuilder.group(
      {
        token: [
          '',
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(6),
          ],
        ],
        senha: ['', [Validators.required, this.validadorSenhaCompleta]],
        confirmarSenha: ['', [Validators.required]],
      },
      {
        validators: this.validadorSenhasIguais,
      }
    );

    // Configurar validadores dinâmicos baseados no tipo de recuperação
    this.atualizarValidadores();
  }

  private validadorSenhaCompleta = (control: any) => {
    const senha = control.value;
    if (!senha) return null;

    this.validarRequisitosSenha(senha);

    if (!this.senhaValida) {
      return { senhaIncompleta: true };
    }

    return null;
  };
  private configurarValidacaoSenha(): void {
    this.formTokenSenhas
      .get('senha')
      ?.valueChanges.subscribe((senha: string) => {
        this.validarRequisitosSenha(senha);
      });

    this.formTokenSenhas.get('confirmarSenha')?.valueChanges.subscribe(() => {
      this.validarConfirmacaoSenha();
    });
  }

  // ========================================
  // MÉTODOS DE TOGGLE
  // ========================================
  alternarTipoRecuperacao(): void {
    const novoTipo: TipoRecuperacao =
      this.tipoRecuperacao() === 'telefone' ? 'email' : 'telefone';
    this.tipoRecuperacao.set(novoTipo);
    this.atualizarValidadores();
    this.limparFormularioContato();
  }

  private atualizarValidadores(): void {
    const telefoneControl = this.formContato.get('telefone');
    const emailControl = this.formContato.get('email');

    if (this.tipoRecuperacao() === 'telefone') {
      telefoneControl?.setValidators([
        Validators.required,
        Validators.pattern(/^\(\d{2}\)\s\d{4,5}-\d{4}$/),
      ]);
      emailControl?.clearValidators();
    } else {
      emailControl?.setValidators([Validators.required, Validators.email]);
      telefoneControl?.clearValidators();
    }

    telefoneControl?.updateValueAndValidity();
    emailControl?.updateValueAndValidity();
  }

  private limparFormularioContato(): void {
    this.formContato.patchValue({
      telefone: '',
      email: '',
    });
    this.formContato.markAsUntouched();
  }

  // ========================================
  // VALIDADORES CUSTOMIZADOS
  // ========================================
  private validadorSenhasIguais = (formGroup: FormGroup) => {
    const senha = formGroup.get('senha')?.value;
    const confirmarSenha = formGroup.get('confirmarSenha')?.value;

    if (senha && confirmarSenha && senha !== confirmarSenha) {
      formGroup.get('confirmarSenha')?.setErrors({ passwordsMismatch: true });
      return { passwordsMismatch: true };
    }

    return null;
  };

  // ========================================
  // MÉTODOS DE VALIDAÇÃO
  // ========================================
  private validarRequisitosSenha(senha: string): void {
    if (!senha) {
      this.resetarValidacaoSenha();
      return;
    }

    this.hasLowerCase.set(/[a-z]/.test(senha));
    this.hasUpperCase.set(/[A-Z]/.test(senha));
    this.hasNumber.set(/[0-9]/.test(senha));
    this.hasSpecialChar.set(/[!@#$%^&*]/.test(senha));
    this.hasMinLength.set(senha.length >= 8);
  }

  private resetarValidacaoSenha(): void {
    this.hasLowerCase.set(false);
    this.hasUpperCase.set(false);
    this.hasNumber.set(false);
    this.hasSpecialChar.set(false);
    this.hasMinLength.set(false);
  }

  private validarConfirmacaoSenha(): void {
    const senha = this.formTokenSenhas.get('senha')?.value;
    const confirmarSenha = this.formTokenSenhas.get('confirmarSenha')?.value;

    if (confirmarSenha && senha !== confirmarSenha) {
      this.formTokenSenhas
        .get('confirmarSenha')
        ?.setErrors({ passwordsMismatch: true });
    } else if (confirmarSenha && senha === confirmarSenha) {
      const errors = this.formTokenSenhas.get('confirmarSenha')?.errors;
      if (errors) {
        delete errors['passwordsMismatch'];
        const hasOtherErrors = Object.keys(errors).length > 0;
        this.formTokenSenhas
          .get('confirmarSenha')
          ?.setErrors(hasOtherErrors ? errors : null);
      }
    }
  }

  // ========================================
  // MÉTODOS DE SUBMISSÃO
  // ========================================
  async onSubmitContato(): Promise<void> {
    if (this.formContato.invalid) {
      this.formContato.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);

    try {
      const dadosContato = this.obterDadosContato();

      this.notificacaoService.notificacaoWarn(
        this.translate.instant(
          'PAGINA_RECUPERAR_SENHA.MENSGEM_NOTIFICACAO_REDEFINIR_SENHA'
        )
      );

      await this.recuperarSenhaService.recuperarToken(dadosContato);

      this.notificacaoService.notificacaoSuccess(
        this.translate.instant(
          'PAGINA_RECUPERAR_SENHA.MENSAGEM_SUCESSO_RECUPERAR_SENHA'
        )
      );

      this.active = 1;
    } catch (error: any) {
      this.notificacaoService.notificacaoError(
        error?.error?.message || 'Erro ao enviar código de verificação'
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  async onSubmitTokenSenha(): Promise<void> {
    if (this.formTokenSenhas.invalid) {
      this.formTokenSenhas.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);

    try {
      this.notificacaoService.notificacaoWarn(
        this.translate.instant(
          'PAGINA_RECUPERAR_SENHA.MENSAGEM_NOTIFICACAO_SENHA_REDEFINIDA'
        )
      );

      const redefinirSenha = this.criarModelRedefinirSenha();
      await this.recuperarSenhaService.alterarSenha(redefinirSenha);

      this.exibirConfirmacaoSucesso();
    } catch (error: any) {
      this.notificacaoService.notificacaoError(
        error?.error?.errorMessage || 'Erro ao redefinir senha'
      );
    } finally {
      this.isLoading.set(false);
    }
  }

  // ========================================
  // MÉTODOS AUXILIARES
  // ========================================
  private obterDadosContato(): string {
    if (this.tipoRecuperacao() === 'telefone') {
      const telefone = this.formContato.get('telefone')?.value;
      return ToolsService.removerMascara(telefone);
    } else {
      return this.formContato.get('email')?.value;
    }
  }

  private criarModelRedefinirSenha(): RedefinirSenhaModel {
    const contatoData = this.formContato.getRawValue();
    const tokenSenhaData = this.formTokenSenhas.getRawValue();

    return {
      telefone:
        this.tipoRecuperacao() === 'telefone' ? contatoData.telefone : '',
      email: this.tipoRecuperacao() === 'email' ? contatoData.email : '',
      token: tokenSenhaData.token,
      senha: tokenSenhaData.senha,
      confirmarSenha: tokenSenhaData.confirmarSenha,
    };
  }

  private exibirConfirmacaoSucesso(): void {
    this.confirmationService.confirm({
      header: this.translate.instant(
        'PAGINA_RECUPERAR_SENHA.TITULO_SENHA_REDEFINIDA'
      ),
      message: this.translate.instant(
        'PAGINA_RECUPERAR_SENHA.MENSAGEM_SUCESSO_SENHA_REDEFINIDA'
      ),
      accept: () => {
        this.router.navigate(['/login']);
      },
    });
  }

  // ========================================
  // MÉTODOS DE NAVEGAÇÃO
  // ========================================
  cancelar(): void {
    this.router.navigate(['/login']);
  }

  voltarEtapa(): void {
    if (this.active > 0) {
      this.active = this.active - 1;
    }
  }

  // ========================================
  // GETTERS PARA TEMPLATE
  // ========================================
  get isBotaoSalvar(): boolean {
    return this.isLoading();
  }

  get senhaValida(): boolean {
    return (
      this.hasLowerCase() &&
      this.hasUpperCase() &&
      this.hasNumber() &&
      this.hasSpecialChar() &&
      this.hasMinLength()
    );
  }

  get senhasIguais(): boolean {
    const senha = this.formTokenSenhas.get('senha')?.value;
    const confirmarSenha = this.formTokenSenhas.get('confirmarSenha')?.value;
    return senha && confirmarSenha && senha === confirmarSenha;
  }

  get campoContatoValido(): boolean {
    if (this.tipoRecuperacao() === 'telefone') {
      return this.formContato.get('telefone')?.valid || false;
    } else {
      return this.formContato.get('email')?.valid || false;
    }
  }

  get textoTipoRecuperacao(): string {
    return this.tipoRecuperacao() === 'telefone' ? 'Telefone' : 'E-mail';
  }

  get placeholderContato(): string {
    return this.tipoRecuperacao() === 'telefone'
      ? '(00) 00000-0000'
      : 'seu@email.com';
  }

  get autocompleteContato(): string {
    return this.tipoRecuperacao() === 'telefone' ? 'tel' : 'email';
  }
}
